import { useCallback, useEffect, useMemo, useState } from "react"

import { Select, Input, InputGroup, InputGroupText } from "twyne-new.ui"

import { useResponsive, useUtilities } from "@/modules/cms/views/post/components/builder/context"

import { useCraftProp } from "../utils"
import { UNIT_OPTIONS } from "../utils/predefined_sizes"

const PredefinedArbitraryUnit = props => {
  const { name = '', value: valueProps, onChange, predefined, predefinedOptions = [], unitOptions = UNIT_OPTIONS, defaultUnit, className = '' } = props

  const { value: craftValue, setValue: setCraftValue } = useCraftProp(name)

  const value = useMemo(() => (name ? craftValue : valueProps) || '', [name, craftValue, valueProps])

  const { unit, onChangeUnit, isArbitrary, onChangeArbitrary } = useUnit({ value, unitOptions, defaultUnit, predefined, predefinedOptions, onChangeValue: setCraftValue })

  const handleChange = value => {
    value = value.replace('_unitless', '')
    name ? setCraftValue(value) : onChange(value)
  }

  const handleChangePredefined = option => {
    const arbitrary = option?.value === 'arbitrary'
    onChangeArbitrary(!!arbitrary)
    const newValue = arbitrary || !option ? '' : option?.value
    handleChange(newValue)
  }

  const handleChangeInput = (inputValue, newUnit) => {
    const newValue = onChangeInput({ inputValue, unit, newUnit })
    handleChange(newValue)
  }

  const handleChangeUnit = option => {
    onChangeUnit(option.value)
    handleChangeInput(value, option.value)
  }

  const inputValue = useMemo(() => getValue(value, unit), [value, unit])

  return <>
    <div className={`flex flex-col gap-1 w-full ${className}`}>
      {predefined && <>
        <Select
          options={[{ label: 'Arbitrary', value: 'arbitrary' }, ...predefinedOptions]}
          value={isArbitrary ? 'arbitrary' : value}
          onChange={handleChangePredefined}
          clearable
          variant='accent'
        />
      </>}

      {isArbitrary && <>
        <InputGroup>
          <Input
            value={inputValue}
            onChange={e => handleChangeInput(e.target.value)}
            type='text'
            variant='accent'
          />
          <Select
            options={unitOptions}
            value={unit}
            onChange={handleChangeUnit}
            variant='accent'
          />
        </InputGroup>
      </>}
    </div>
  </>
}

const ArbitraryUnit = props => {
  const { name = '', value: valueProps, onChange, unitOptions = UNIT_OPTIONS, defaultUnit } = props

  const { value: craftValue, setValue: setCraftValue } = useCraftProp(name)

  const value = useMemo(() => (name ? craftValue : valueProps) || '', [name, craftValue, valueProps])

  const { unit, onChangeUnit } = useUnit({ value, unitOptions, defaultUnit, onChangeValue: setCraftValue })

  const handleChange = value => {
    value = value.replace('_unitless', '')
    name ? setCraftValue(value) : onChange(value)
  }

  const handleChangeInput = (inputValue, newUnit) => {
    const newValue = onChangeInput({ inputValue, unit, newUnit })
    handleChange(newValue)
  }

  const handleChangeUnit = option => {
    onChangeUnit(option.value)
    handleChangeInput(value, option.value)
  }

  const inputValue = useMemo(() => getValue(value, unit), [value, unit])

  return <>
    <InputGroup>
      <Input
        value={inputValue}
        onChange={e => handleChangeInput(e.target.value)}
        type='text'
        variant='accent'
      />
      <Select
        options={unitOptions}
        value={unit}
        onChange={handleChangeUnit}
        variant='accent'
      />
    </InputGroup>
  </>
}

const Unit = props => {
  const { name = '', value: valueProps, onChange, unitOptions = UNIT_OPTIONS, defaultUnit } = props

  const { value: craftValue, setValue: setCraftValue } = useCraftProp(name)

  const value = useMemo(() => (name ? craftValue : valueProps) || '', [name, craftValue, valueProps])

  const { unit, onChangeUnit } = useUnit({ value, unitOptions, defaultUnit, onChangeValue: setCraftValue })

  const handleChange = value => {
    value = value.replace('_unitless', '')
    name ? setCraftValue(value) : onChange(value)
  }

  const handleChangeInput = (inputValue, newUnit) => {
    const newValue = onChangeInput({ inputValue, unit, newUnit })
    handleChange(newValue)
  }

  const handleChangeUnit = option => {
    onChangeUnit(option.value)
    handleChangeInput(value, option.value)
  }

  return <>
    <Select
      options={unitOptions}
      value={unit}
      onChange={handleChangeUnit}
      variant='accent'
    />
  </>
}

const ArbitraryInput = props => {
  const { name = '', value: valueProps, onChange, addonPrepend: AddonPrepend, unit } = props

  const { value: craftValue, setValue: setCraftValue } = useCraftProp(name)

  const value = useMemo(() => (name ? craftValue : valueProps) || '', [name, craftValue, valueProps])

  const handleChange = useCallback(value => {
    value = value.replace('_unitless', '')
    name ? setCraftValue(value) : onChange(value)
  }, [name, setCraftValue, onChange])

  const handleChangeInput = (inputValue, newUnit) => {
    const newValue = onChangeInput({ inputValue, unit, newUnit })
    handleChange(newValue)
  }

  useEffect(() => {
    const currentUnit = getUnit(value, 'unitless')
    if (value && currentUnit !== unit) {
      const newValue = [value.split(UNIT_SEPARATOR)[0], unit].join(UNIT_SEPARATOR)
      handleChange(newValue)
    }
  }, [unit, value, setCraftValue, craftValue, handleChange])

  const inputValue = useMemo(() => getValue(value, unit), [value, unit])

  return <>
    <InputGroup variant='accent'>
      {AddonPrepend && <>
        <InputGroupText addonType='prepend'>
          {AddonPrepend}
        </InputGroupText>
      </>}

      <Input
        value={inputValue}
        onChange={e => handleChangeInput(e.target.value)}
        type='text'
        variant='accent'
      />
    </InputGroup>
  </>
}

const useUnit = ({ value, unitOptions, defaultUnit = unitOptions[0].value, predefined, predefinedOptions = [], onChangeValue }) => {
  const { device } = useResponsive()
  const { selectedUtilities } = useUtilities()

  useEffect(() => {
    setIsArbitrary()
  }, [device, selectedUtilities])

  const [unit, setUnit] = useState(defaultUnit)
  const [isArbitrary, setIsArbitrary] = useState()

  useEffect(() => {
    const newUnit = getUnit(value, defaultUnit)
    if (unit !== newUnit && value) {
      setUnit(newUnit)
    }
  }, [value, unit, defaultUnit])

  useEffect(() => {
    if (isArbitrary === undefined) {
      const valueToUse = convertOldValue(value, unitOptions)
      const arbitrary = !predefined || (valueToUse && !predefinedOptions.map(item => item.value).includes(valueToUse))
      setIsArbitrary(arbitrary)
      arbitrary && setUnit(getUnit(valueToUse, defaultUnit))
      valueToUse !== value && onChangeValue(valueToUse)
    }
  }, [value, isArbitrary, predefined, predefinedOptions, unitOptions, defaultUnit, onChangeValue])

  return { onChangeUnit: setUnit, unit, onChangeArbitrary: setIsArbitrary, isArbitrary }
}

const convertOldValue = (oldValue, unitOptions) => {
  const unitValues = unitOptions.map(item => item.value)
  const newUnit = unitValues.find(item => oldValue.includes(item)) || 'unitless'
  const parseValue = oldValue.replace(newUnit, '').replace(UNIT_SEPARATOR, '')
  const parseByUnit = `${newUnit === 'unitless' ? '' : `${UNIT_SEPARATOR}${newUnit}`}`
  const parsed = `${parseValue}${parseByUnit}` || ''
  const [prefix, value] = parsed.replace('[', '').replace(']', '').split('-')
  return prefix && value ? value : prefix
}

const getUnit = (value = '', defaultUnit) => value.split(UNIT_SEPARATOR)[1] || defaultUnit

const getValue = (value = '', unit) => value.split(UNIT_SEPARATOR)[0].replace(unit, '')

const onChangeInput = ({ inputValue, unit, newUnit }) => {
  const sanitizedByUnit = unit === 'unitless' ? inputValue.replace(/(?!^-)[^0-9/]/g, '') : inputValue.replace(/(?!^-)[^0-9./]/g, '')
  const sanitized = sanitizedByUnit.includes('/') ? sanitizedByUnit.replace(/\.{2,}/g, '.').replace(/\/.*\//g, match => match.replace(/\/+$/, '')) : sanitizedByUnit.replace(/\.{2,}/g, '.').replace(/\/{2,}/g, '/')
  const newValue = sanitized ? `${sanitized.split(UNIT_SEPARATOR)[0]}${UNIT_SEPARATOR}${newUnit || unit}` : ''
  return newValue
}

const UNIT_SEPARATOR = '_'

export {
  PredefinedArbitraryUnit,
  ArbitraryUnit,
  ArbitraryInput,
  Unit,
  getUnit,
  getValue,
}
